import { apiClient } from './api';

// ---- Typen ----

export type UnitType = 'class' | 'course' | 'group' | 'ad_hoc';
export type AttendanceStatus = 'present' | 'late' | 'excused' | 'absent' | 'beurlaubt';
export type ExcuseKind = 'excuse' | 'beurlaubung' | 'sick';
export type ExcuseStatus = 'pending' | 'approved' | 'rejected';

export interface KlassenbuchEntry {
  id: number;
  uid: string;
  title: string;
  topic_summary: string | null;
  parent_type: UnitType;
  parent_id: number | null;
  subject_id?: number | null;
  subject_name?: string | null;
  subject_code?: string | null;
  status: string;
  start_at: string | null;
  end_at: string | null;
  has_homework: number | boolean;
  homework_due_date: string | null;
  is_substitution: number;
  substitution_for_user_id: number | null;
  substitution_reason?: string | null;
  klassenbuch_locked_at: string | null;
  source?: 'manual' | 'timetable' | 'ad_hoc' | string | null;
  is_cancelled?: number | boolean;
  has_manual_content?: number | boolean;
  attendance_count: number;
  absent_count: number;
  late_count: number;
  excused_count: number;
  beurlaubt_count: number;
}

export interface KlassenbuchParticipant {
  user_id: number;
  username: string;
  schueler_table_id?: number | null;
  first_name?: string | null;
  last_name?: string | null;
  sophomorixUnid?: string | null;
  Student_ID?: string | null;
  Vorname?: string | null;
  Name?: string | null;
  Klasse?: string | null;
  attendance_status: AttendanceStatus | null;
  arrival_time: string | null;
  attendance_notes: string | null;
  excuse_text: string | null;
  excuse_id: number | null;
  marked_at: string | null;
  marked_by: number | null;
  via: string | null;
  active_excuses: Excuse[];
}

export interface LinkedClassEntry {
  id: number;
  parent_type: UnitType;
  parent_id: number | null;
  class_name?: string | null;
  course_name?: string | null;
}

export interface CourseClassLink {
  id: number;
  course_id: number;
  class_id: number;
  class_name: string;
  source: 'manual' | 'timetable';
}

export interface KlassenbuchDetail {
  unit: {
    id: number;
    uid: string;
    title: string;
    topic_summary: string | null;
    lesson_content_html: string | null;
    homework_html: string | null;
    homework_due_date: string | null;
    is_substitution: number;
    substitution_for_user_id: number | null;
    substitution_reason?: string | null;
    is_cancelled?: number | boolean;
    klassenbuch_locked_at: string | null;
    klassenbuch_locked_by: number | null;
    parent_type: UnitType;
    parent_id: number | null;
    class_name?: string | null;
    course_name?: string | null;
    group_name?: string | null;
    start_at: string | null;
    end_at: string | null;
  };
  participants: KlassenbuchParticipant[];
  acknowledgments: Acknowledgment[];
  linkedClassEntries?: LinkedClassEntry[];
  courseClassLinks?: CourseClassLink[];
  participantSource?: string | null;
  attendanceTeachingUnitId?: number | null;
  courseId?: number | null;
}

export interface Excuse {
  id: number;
  uid: string;
  student_id: number;
  kind: ExcuseKind;
  valid_from: string;
  valid_to: string;
  reason: string | null;
  document_file_id: number | null;
  approval_status: ExcuseStatus;
  approved_by: number | null;
  approved_at: string | null;
  source: string;
  created_by: number;
  created_at: string;
  updated_at: string;
  username?: string;
  first_name?: string | null;
  last_name?: string | null;
}

export interface Acknowledgment {
  id: number;
  uid: string;
  target_type: 'teaching_unit' | 'homework' | 'excuse' | 'announcement';
  target_id: number;
  acknowledger_kind: 'parent' | 'student' | 'teacher' | 'schulleitung';
  acknowledger_ref: string;
  token: string;
  token_expires_at: string | null;
  acknowledged_at: string | null;
  ip_address?: string | null;
  created_at: string;
}

export interface AuditEntry {
  id: number;
  entity_type: string;
  entity_id: number;
  action: string;
  old_values: Record<string, unknown> | null;
  new_values: Record<string, unknown> | null;
  actor_user_id: number | null;
  actor_username?: string | null;
  actor_name?: string | null;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

export interface StatsRow {
  student_id: string;
  present_cnt: number;
  late_cnt: number;
  absent_cnt: number;
  excused_cnt: number;
  beurlaubt_cnt: number;
  total_cnt: number;
}

export interface KlassenbuchTeacher {
  id: number;
  username: string;
  first_name?: string | null;
  last_name?: string | null;
  role?: string | null;
}

export interface SubstitutionAvailabilitySlot {
  id?: number;
  teacher_id?: number;
  weekday: number;
  period_no: number;
  location?: string | null;
  note?: string | null;
}

export interface CurriculumLink {
  id?: number;
  curriculum_id: number;
  content_field_id?: number | null;
  competency_id?: number | null;
}

export interface CompetencySuggestion {
  competency_id: number;
  competency_name: string;
  curriculum_id: number;
}

export interface StaffNote {
  id: number;
  scope_type: 'class' | 'course' | 'group' | 'student' | 'teaching_unit';
  scope_id: string;
  title: string | null;
  note_text: string;
  notify_email: number;
  is_resolved: number;
  created_by: number | null;
  created_at: string;
}

export interface SchoolEvent {
  id: number;
  uid: string;
  title: string;
  description: string | null;
  starts_at: string;
  ends_at: string;
  target_scope: 'school' | 'class' | 'course' | 'group';
  target_id: number | null;
  room: string | null;
}

export type SchoolDay = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

export interface SchedulePeriod {
  period_no: number;
  label: string | null;
  start_time: string;
  end_time: string;
  is_break: boolean;
}

export interface ScheduleConfig {
  schoolDays: SchoolDay[];
  dayStart: string;
  lessonsPerDay: number;
  defaultLessonMinutes: number;
  defaultBreakMinutes: number;
  periods: SchedulePeriod[];
}

export interface ScheduleGrid extends ScheduleConfig {
  id: string;
  name: string;
}

export interface ScheduleConfigDocument {
  version: number;
  defaultGridId: string;
  grids: ScheduleGrid[];
}

export interface SchedulePeriodsResponse {
  source: 'active_timetable' | 'schedule_config';
  periods: SchedulePeriod[];
}

export interface WeekScheduleSlot {
  date: string;
  period_no: number;
  timetable_entry_id: number;
  subject_id: number | null;
  subject_code: string | null;
  title: string;
  start_time: string | null;
  end_time: string | null;
}

export interface KlassenbuchEmailNotificationsConfig {
  enabled: boolean;
}

export type ImportSource = 'untis' | 'davinci' | 'csv' | 'generate-from-config';

/** Fach-ID (String) → #rrggbb — gespeichert unter Einstellungen klassenbuch.subject_colors */
export type SubjectColorMap = Record<string, string>;

const DEFAULT_SCHEDULE_CONFIG: ScheduleConfig = {
  schoolDays: ['mon', 'tue', 'wed', 'thu', 'fri'],
  dayStart: '08:00',
  lessonsPerDay: 8,
  defaultLessonMinutes: 45,
  defaultBreakMinutes: 5,
  periods: []
};

const DEFAULT_GRID_ID = 'grid-default';

function normalizeScheduleConfig(raw: any): ScheduleConfig {
  return {
    ...DEFAULT_SCHEDULE_CONFIG,
    ...(raw && typeof raw === 'object' ? raw : {}),
    periods: Array.isArray(raw?.periods) ? raw.periods : []
  };
}

export function normalizeScheduleConfigDocument(raw: any): ScheduleConfigDocument {
  if (raw && typeof raw === 'object' && Array.isArray(raw.grids)) {
    const grids = raw.grids
      .filter((grid: any) => grid && typeof grid === 'object')
      .map((grid: any, idx: number): ScheduleGrid => ({
        id: String(grid.id || `grid-${idx + 1}`).slice(0, 36),
        name: String(grid.name || `Raster ${idx + 1}`).slice(0, 80),
        ...normalizeScheduleConfig(grid)
      }));
    const safeGrids: ScheduleGrid[] = grids.length
      ? grids
      : [{ id: DEFAULT_GRID_ID, name: 'Standard', ...DEFAULT_SCHEDULE_CONFIG }];
    const requestedDefault = String(raw.defaultGridId || '').trim();
    const hasRequested = safeGrids.some((grid) => grid.id === requestedDefault);
    return {
      version: Number(raw.version) || 2,
      defaultGridId: hasRequested ? requestedDefault : safeGrids[0].id,
      grids: safeGrids
    };
  }

  return {
    version: 2,
    defaultGridId: DEFAULT_GRID_ID,
    grids: [{ id: DEFAULT_GRID_ID, name: 'Standard', ...normalizeScheduleConfig(raw) }]
  };
}

export function getScheduleGridById(doc: ScheduleConfigDocument, gridId?: string | null): ScheduleGrid {
  if (gridId) {
    const byId = doc.grids.find((grid) => grid.id === gridId);
    if (byId) return byId;
  }
  return doc.grids.find((grid) => grid.id === doc.defaultGridId) || doc.grids[0];
}

export interface KlassenbuchSubjectRow {
  id: number;
  subject_name: string;
  subject_code: string | null;
}

// ---- API ----

export const klassenbuchApi = {
  async listEntries(params: {
    unitType: UnitType | UnitType[];
    unitId: number | number[];
    schoolYearId?: number | null;
    from?: string | null;
    to?: string | null;
  }): Promise<KlassenbuchEntry[]> {
    const qs: Record<string, string> = {
      unit_type: Array.isArray(params.unitType) ? params.unitType.join(',') : params.unitType,
      unit_id: Array.isArray(params.unitId) ? params.unitId.join(',') : String(params.unitId)
    };
    if (params.schoolYearId) qs.school_year_id = String(params.schoolYearId);
    if (params.from) qs.from = params.from;
    if (params.to) qs.to = params.to;
    const res = await apiClient.get('/klassenbuch/entries', { params: qs });
    return res.data.data;
  },

  async getEntry(teachingUnitId: number): Promise<KlassenbuchDetail> {
    const res = await apiClient.get(`/klassenbuch/entries/${teachingUnitId}`);
    return res.data.data;
  },

  async createEntry(payload: {
    parent_type: UnitType;
    parent_id: number;
    date: string;
    period_no?: number;
    title?: string;
    topic_summary?: string;
    start_at?: string;
    end_at?: string;
  }): Promise<{ id: number; title: string; start_at: string; end_at: string }> {
    const res = await apiClient.post('/klassenbuch/entries', payload);
    return res.data.data;
  },

  async ensureWeekEntries(payload: {
    parent_type: UnitType;
    parent_id: number;
    week_start: string;
    week_end: string;
    school_year_id?: number | null;
  }): Promise<{ createdCount: number; createdIds: number[]; consideredDates: string[] }> {
    const res = await apiClient.post('/klassenbuch/entries/ensure-week', payload);
    return res.data.data;
  },

  async getWeekScheduleSlots(payload: {
    parent_type: UnitType;
    parent_id: number;
    week_start: string;
    week_end: string;
    school_year_id?: number | null;
  }): Promise<WeekScheduleSlot[]> {
    const res = await apiClient.get('/klassenbuch/schedule/week-slots', { params: payload });
    return res.data.data;
  },

  async updateEntry(
    teachingUnitId: number,
    fields: Partial<{
      title: string;
      topic_summary: string | null;
      lesson_content_html: string | null;
      homework_html: string | null;
      homework_due_date: string | null;
      is_substitution: boolean | 0 | 1;
      substitution_for_user_id: number | null;
      substitution_reason?: string | null;
      is_cancelled: boolean | 0 | 1;
    }>
  ) {
    const res = await apiClient.patch(`/klassenbuch/entries/${teachingUnitId}`, fields);
    return res.data.data;
  },

  async lock(teachingUnitId: number) {
    const res = await apiClient.post(`/klassenbuch/entries/${teachingUnitId}/lock`, {});
    return res.data.data;
  },
  async unlock(teachingUnitId: number) {
    const res = await apiClient.post(`/klassenbuch/entries/${teachingUnitId}/unlock`, {});
    return res.data.data;
  },

  async setAttendance(
    teachingUnitId: number,
    studentKey: string,
    payload: {
      status: AttendanceStatus;
      arrival_time?: string | null;
      notes?: string | null;
      excuse_text?: string | null;
      excuse_id?: number | null;
      via?: 'manual' | 'bulk' | 'nfc';
    }
  ) {
    const res = await apiClient.put(
      `/klassenbuch/attendance/${teachingUnitId}/${encodeURIComponent(studentKey)}`,
      payload
    );
    return res.data.data;
  },

  async setAttendanceBulk(
    teachingUnitId: number,
    payload: { status: AttendanceStatus; student_keys: string[]; change_reason?: string | null }
  ) {
    const res = await apiClient.put(`/klassenbuch/attendance/${teachingUnitId}/bulk`, payload);
    return res.data.data as { count: number };
  },

  async previousHomework(teachingUnitId: number) {
    const res = await apiClient.get(`/klassenbuch/entries/${teachingUnitId}/previous-homework`);
    return res.data.data as {
      id: number;
      title: string;
      homework_html: string | null;
      homework_due_date: string | null;
      start_at: string | null;
    } | null;
  },

  async listExcuses(params: {
    studentId?: number;
    from?: string;
    to?: string;
    status?: ExcuseStatus;
  }): Promise<Excuse[]> {
    const res = await apiClient.get('/klassenbuch/excuses', {
      params: {
        student_id: params.studentId,
        from: params.from,
        to: params.to,
        status: params.status
      }
    });
    return res.data.data;
  },
  async createExcuse(payload: {
    student_id: number;
    kind: ExcuseKind;
    valid_from: string;
    valid_to: string;
    reason?: string;
    document_file_id?: number | null;
    source?: 'parent' | 'teacher' | 'admin' | 'system';
  }): Promise<Excuse> {
    const res = await apiClient.post('/klassenbuch/excuses', payload);
    return res.data.data;
  },
  async approveExcuse(excuseId: number, approve = true) {
    const res = await apiClient.patch(`/klassenbuch/excuses/${excuseId}/approve`, { approve });
    return res.data.data;
  },

  async notifyHomework(teachingUnitId: number) {
    const res = await apiClient.post(`/klassenbuch/homework/${teachingUnitId}/notify`, {});
    return res.data.data as { sent: number; recipientCount: number };
  },

  async createAnnouncement(payload: {
    unit_type: UnitType;
    unit_id: number;
    subject: string;
    text: string;
    require_ack?: boolean;
  }) {
    const res = await apiClient.post('/klassenbuch/announcements', payload);
    return res.data.data as {
      announcementId: number;
      sent: number;
      recipientCount: number;
      acks: { id: number; uid: string; token: string }[];
    };
  },

  async createAcknowledgmentRequests(payload: {
    target_type: 'teaching_unit' | 'homework' | 'excuse' | 'announcement';
    target_id: number;
    recipients: { kind: 'parent' | 'student' | 'teacher'; ref: string }[];
    valid_days?: number;
  }) {
    const res = await apiClient.post('/klassenbuch/acknowledgments', payload);
    return res.data.data as { id: number; uid: string; token: string }[];
  },

  async listAcknowledgments(targetType: string, targetId: number) {
    const res = await apiClient.get('/klassenbuch/acknowledgments', {
      params: { target_type: targetType, target_id: targetId }
    });
    return res.data.data as Acknowledgment[];
  },

  async audit(entityType: string, entityId: number, limit = 100): Promise<AuditEntry[]> {
    const res = await apiClient.get(`/klassenbuch/audit/${entityType}/${entityId}`, {
      params: { limit }
    });
    return res.data.data;
  },

  async statistics(params: { unit_type: UnitType; unit_id: number; school_year_id?: number }) {
    const res = await apiClient.get('/klassenbuch/statistics', { params });
    return res.data.data as StatsRow[];
  },

  async absenceDigest(params: { unit_type: UnitType; unit_id: number; from?: string; to?: string }) {
    const res = await apiClient.get('/klassenbuch/statistics/absences-digest', { params });
    return res.data.data as Array<{
      day: string;
      absent_cnt: number;
      late_cnt: number;
      excused_cnt: number;
      beurlaubt_cnt: number;
    }>;
  },

  async studentWeek(from?: string, to?: string): Promise<KlassenbuchEntry[]> {
    const res = await apiClient.get('/klassenbuch/student/week', { params: { from, to } });
    return res.data.data;
  },

  async getConfig(): Promise<ScheduleConfigDocument> {
    const res = await apiClient.get('/klassenbuch/config');
    return normalizeScheduleConfigDocument(res.data.data);
  },

  async getSchedulePeriods(params: {
    parent_type: UnitType;
    parent_id: number;
    date: string;
  }): Promise<SchedulePeriodsResponse> {
    const res = await apiClient.get('/klassenbuch/schedule/periods', { params });
    return {
      source: res.data?.data?.source === 'active_timetable' ? 'active_timetable' : 'schedule_config',
      periods: Array.isArray(res.data?.data?.periods) ? res.data.data.periods : []
    };
  },

  async saveConfig(cfg: ScheduleConfigDocument): Promise<ScheduleConfigDocument> {
    const res = await apiClient.put('/klassenbuch/config', cfg);
    return normalizeScheduleConfigDocument(res.data.data);
  },

  async getEmailNotificationsConfig(): Promise<KlassenbuchEmailNotificationsConfig> {
    const res = await apiClient.get('/klassenbuch/email-notifications-config');
    return { enabled: !!res.data?.data?.enabled };
  },

  async saveEmailNotificationsConfig(enabled: boolean): Promise<KlassenbuchEmailNotificationsConfig> {
    const res = await apiClient.put('/klassenbuch/email-notifications-config', { enabled });
    return { enabled: !!res.data?.data?.enabled };
  },

  async getSubjectColors(): Promise<{ colors: SubjectColorMap; subjects: KlassenbuchSubjectRow[] }> {
    const res = await apiClient.get('/klassenbuch/subject-colors');
    return res.data.data;
  },

  async listTeachers(): Promise<KlassenbuchTeacher[]> {
    const res = await apiClient.get('/klassenbuch/teachers');
    return res.data.data;
  },

  async getSubstitutionAvailability(teacherId: number): Promise<SubstitutionAvailabilitySlot[]> {
    const res = await apiClient.get('/klassenbuch/substitution-availability', {
      params: { teacher_id: teacherId }
    });
    return res.data.data;
  },

  async saveSubstitutionAvailability(teacherId: number, slots: SubstitutionAvailabilitySlot[]) {
    const res = await apiClient.put('/klassenbuch/substitution-availability', {
      teacher_id: teacherId,
      slots
    });
    return res.data.data;
  },

  async listSubstitutions(from?: string, to?: string) {
    const res = await apiClient.get('/klassenbuch/substitutions', { params: { from, to } });
    return res.data.data;
  },

  async createSubstitution(payload: {
    date: string;
    period_no: number;
    absent_teacher_id: number;
    substitute_teacher_id: number;
    teaching_unit_id?: number | null;
    status?: 'planned' | 'confirmed' | 'cancelled' | 'done';
    reason?: string | null;
    note?: string | null;
  }) {
    const res = await apiClient.post('/klassenbuch/substitutions', payload);
    return res.data.data;
  },

  async getCurriculumLinks(teachingUnitId: number): Promise<CurriculumLink[]> {
    const res = await apiClient.get(`/klassenbuch/entries/${teachingUnitId}/curriculum-links`);
    return res.data.data;
  },

  async saveCurriculumLinks(teachingUnitId: number, links: CurriculumLink[]) {
    const res = await apiClient.put(`/klassenbuch/entries/${teachingUnitId}/curriculum-links`, { links });
    return res.data.data;
  },

  async suggestCompetencies(teachingUnitId: number, limit = 12): Promise<CompetencySuggestion[]> {
    const res = await apiClient.post(`/klassenbuch/entries/${teachingUnitId}/competencies/suggest`, { limit });
    return res.data.data;
  },

  async applyCompetencies(teachingUnitId: number, competencyIds: number[]) {
    const res = await apiClient.post(`/klassenbuch/entries/${teachingUnitId}/competencies/apply`, {
      competency_ids: competencyIds
    });
    return res.data.data as { inserted: number };
  },

  async listUnitGrades(teachingUnitId: number) {
    const res = await apiClient.get(`/klassenbuch/gradebook/${teachingUnitId}`);
    return res.data.data;
  },

  async createUnitGrade(
    teachingUnitId: number,
    payload: {
      student_id: string;
      title: string;
      grade_value: string;
      graded_at: string;
      status?: 'draft' | 'final' | 'published' | 'archived';
    }
  ) {
    const res = await apiClient.post(`/klassenbuch/gradebook/${teachingUnitId}`, payload);
    return res.data.data;
  },

  async getStudentTimeline(studentKey: string, from?: string, to?: string) {
    const res = await apiClient.get(`/klassenbuch/student/${encodeURIComponent(studentKey)}/timeline`, {
      params: { from, to }
    });
    return res.data.data as {
      attendance: Array<Record<string, unknown>>;
      notes: Array<Record<string, unknown>>;
    };
  },

  async listStaffNotes(params: { scope_type?: string; scope_id?: string }): Promise<StaffNote[]> {
    const res = await apiClient.get('/klassenbuch/staff-notes', { params });
    return res.data.data;
  },

  async createStaffNote(payload: {
    scope_type: 'class' | 'course' | 'group' | 'student' | 'teaching_unit';
    scope_id: string;
    title?: string;
    note_text: string;
    notify_email?: boolean;
  }): Promise<StaffNote> {
    const res = await apiClient.post('/klassenbuch/staff-notes', payload);
    return res.data.data;
  },

  async listEvents(from?: string, to?: string): Promise<SchoolEvent[]> {
    const res = await apiClient.get('/klassenbuch/events', { params: { from, to } });
    return res.data.data;
  },

  async createEvent(payload: {
    title: string;
    description?: string;
    starts_at: string;
    ends_at: string;
    target_scope?: 'school' | 'class' | 'course' | 'group';
    target_id?: number | null;
    room?: string | null;
  }): Promise<SchoolEvent> {
    const res = await apiClient.post('/klassenbuch/events', payload);
    return res.data.data;
  },

  async saveSubjectColors(colors: SubjectColorMap): Promise<SubjectColorMap> {
    const res = await apiClient.put('/klassenbuch/subject-colors', { colors });
    return res.data.data.colors;
  },

  async importSchedule(
    source: ImportSource,
    file?: File | null,
    extra?: Record<string, unknown>
  ) {
    const endpointMap: Record<ImportSource, string> = {
      untis: '/klassenbuch/import/untis',
      davinci: '/klassenbuch/import/davinci',
      csv: '/klassenbuch/import/csv',
      'generate-from-config': '/klassenbuch/generate-from-config'
    };
    const endpoint = endpointMap[source];
    if (file) {
      const form = new FormData();
      form.append('file', file);
      if (extra) {
        for (const [k, v] of Object.entries(extra)) {
          form.append(k, typeof v === 'string' ? v : JSON.stringify(v));
        }
      }
      const res = await apiClient.post(endpoint, form);
      return res.data;
    }
    const res = await apiClient.post(endpoint, extra || {});
    return res.data;
  }
};

export default klassenbuchApi;
